import React, { useEffect, useState } from "react";
import Card from "./_Card";
import Stepper from "../../../components/profile/Stepper";
import Input from "../../../components/ui/Input";
import Select from "../../../components/ui/Select";
import Button from "../../../components/ui/Button";
import UploadDropzone from "../../../components/ui/UploadDropzone";
import Avatar from "../../../components/ui/Avatar";
import { useNavigate } from "react-router-dom";
import ProfileSidebar from "../../../components/reusable components/ProfileSidebar";
import {
  getProfileDetails,
  putProfileDetails,
  uploadProfileFile,
  OrganizationDetails,
  deleteLogo,
  getMetaIndividual,
  Individual,
  getMetaGeographic,
  Geographic,
} from "../../../api/profile";
import {
  getCities,
  getCountries,
  getMobileCodes,
  getStates,
  getOrgTypes,
  getOrgSizes,
  Option,
} from "../../../lib/api";
import toast from "react-hot-toast";
import { useAuth } from "../../../contexts/AuthContext";
import {
  resolveOnboardingRecord,
  updateOnboardingDraft,
} from "../../../features/onboarding/service";
import { valueExistsInOptions } from "./selectHelpers";

/* --- helpers --- */
const ArrowRight = () => <span className="material-icons">arrow_right_alt</span>;

type ProfileForm = {
  legalName: string;
  orgNumber: string;
  foundedYear: string;
  website: string;
  email: string;
  phoneIsd: string;
  phone: string;
  streetAddress: string;
  state: string;
  city: string;
  postalCode: string;
  country: string;
  orgType: string;
  orgSize: string;
  age?: string;
  gender?: string;
  ethnicity?: string;
  disability_status?: string;
  veteran?: string;
  immigration_status?: string;
  socioeconomic_status?: string;
  rural_urban?: string;
  operating_area?: string;
  operatingScope?: string;
};

const toForm = (api: OrganizationDetails): ProfileForm => ({
  legalName: api.company_name ?? "",
  orgNumber: api.organization_number ?? "",
  foundedYear: api.founded_year ? String(api.founded_year) : "",
  website: api.website_url ?? "",
  email: api.contact_email ?? "",
  phoneIsd: "",
  phone: "",
  streetAddress: api.address_line ?? "",
  state: api.state ?? "",
  city: api.city ?? "",
  postalCode: api.postal_code ?? "",
  country: api.region && api.region.trim() ? api.region : "",
  orgType: api.organization_type ?? "",
  orgSize: api.size ?? "",

  age: api.age ? String(api.age) : "",
  gender: api.gender ?? "",
  ethnicity: api.ethnicity ?? "",
  disability_status: api.disability_status ?? "",
  veteran: api.veteran ?? "",
  immigration_status: api.immigration_status ?? "",
  socioeconomic_status: api.socioeconomic_status ?? "",
  rural_urban: api.rural_urban ?? "",
  operating_area: api.operating_area ?? "",
  operatingScope:
    api.operating_area ?? (api.extra_details as any)?.onboarding_operating_scope ?? "",
});

type PhoneRule = { min?: number; max?: number; exact?: number };
const PHONE_RULES: Record<string, PhoneRule> = {
  "+91": { exact: 10 },
  "+1": { exact: 10 },
  "+44": { min: 9, max: 10 },
  "+61": { min: 9, max: 9 },
  "+65": { exact: 8 },
  "+971": { exact: 9 },
  "+81": { min: 9, max: 10 },
  "+49": { min: 10, max: 11 },
  "+33": { exact: 9 },
  "+39": { min: 6, max: 10 },
  "+353": { exact: 9 },
  "+358": { min: 8, max: 10 },
};
function validatePhoneByCode(code: string, national: string) {
  const digits = (national || "").replace(/\D/g, "");
  if (!digits) return { valid: true, message: "" };
  const rule = PHONE_RULES[code];
  if (rule) {
    const ok = rule.exact
      ? digits.length === rule.exact
      : digits.length >= (rule.min ?? 0) && digits.length <= (rule.max ?? 99);
    if (!ok) {
      const need = rule.exact ? `${rule.exact}` : `${rule.min}-${rule.max}`;
      return { valid: false, message: `Phone must be ${need} digits for ${code}.` };
    }
    return { valid: true, message: "" };
  }
  if (digits.length < 6 || digits.length > 15) {
    return { valid: false, message: "Phone must be 6–15 digits." };
  }
  return { valid: true, message: "" };
}

/* --- component --- */
export default function Details() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [apiSeed, setApiSeed] = useState<OrganizationDetails | null>(null);
  const [form, setForm] = useState<ProfileForm>({
    legalName: "",
    orgNumber: "",
    foundedYear: "",
    website: "",
    email: "",
    phoneIsd: "",
    phone: "",
    streetAddress: "",
    state: "",
    city: "",
    postalCode: "",
    country: "",
    orgType: "",
    orgSize: "",
    operatingScope: "",
  });
  const [logoUploading, setLogoUploading] = useState(false);
  const [logoUrl, setLogoUrl] = useState<string | null>(null);

  const [isdOptions, setIsdOptions] = useState<Option[]>([]);
  const [countryOptions, setCountryOptions] = useState<Option[]>([]);
  const [stateOptions, setStateOptions] = useState<Option[]>([]);
  const [cityOptions, setCityOptions] = useState<Option[]>([]);
  const [metaIndividual, setMetaIndividual] = useState<Individual | null>(null);
  const [metaGeographic, setMetaGeographic] = useState<Geographic | null>(null);
  const [saveButton, setSaveButton] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, boolean>>({});

  const [orgTypeOptions, setOrgTypeOptions] = useState<Option[]>([]); 
  const [orgSizeOptions, setOrgSizeOptions] = useState<Option[]>([]); 
  const [phoneError, setPhoneError] = useState<string>("");
  const [ageError, setAgeError] = useState<string>("");
  const isProfileRoute = true;
  const userKey = user?.uid || user?.email || "anonymous";
  const safePhoneIsd = valueExistsInOptions(form.phoneIsd, isdOptions) ? form.phoneIsd : "";
  const safeCountry = valueExistsInOptions(form.country, countryOptions) ? form.country : "";
  const safeState = valueExistsInOptions(form.state, stateOptions) ? form.state : "";
  const safeCity = valueExistsInOptions(form.city, cityOptions) ? form.city : "";
  const safeOrgType = valueExistsInOptions(form.orgType, orgTypeOptions) ? form.orgType : "";
  const safeOrgSize = valueExistsInOptions(form.orgSize, orgSizeOptions) ? form.orgSize : "";
  const hasValue = (value: string, options?: string[]) =>
    !!value && Array.isArray(options) && options.includes(value);

  const syncHeaderAvatar = (url: string | null) => {
    try {
      const raw = localStorage.getItem("roott_user");
      const obj = raw ? JSON.parse(raw) : {};
      obj.profile = obj.profile || {};
      obj.profile.profile_photo = url || null;
      localStorage.setItem("roott_user", JSON.stringify(obj));
      window.dispatchEvent(new Event("roott:profile-photo-updated"));
    } catch { }
  };

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const res = await getProfileDetails();
        if (!active) return;
        setApiSeed(res);
        setForm((s) => ({ ...s, ...toForm(res) }));
        setLogoUrl(res.profile_photo || null);
        syncHeaderAvatar(res.profile_photo || null);
      } catch (e: any) {
        if (!active) return;
        setError(
        e?.response?.data?.error ||
        e?.response?.data?.message ||
        e?.response?.data?.detail || "Failed to load profile details"
        );
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const record = await resolveOnboardingRecord(userKey);
        if (!active) return;
        setForm((current) => ({
          ...current,
          legalName: current.legalName || record.data.organizationName,
          website: current.website || record.data.websiteUrl,
          country: current.country || record.data.country,
          postalCode: current.postalCode || record.data.postcode,
          orgType: current.orgType || record.data.organizationType,
          operatingScope: current.operatingScope || record.data.operatingScope,
          operating_area: current.operating_area || record.data.operatingScope,
        }));
      } catch {
      }
    })();
    return () => {
      active = false;
    };
  }, [userKey]);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const [codes, countries, types, sizes, metaIndividuals, metaGeographics,] = await Promise.all([
          getMobileCodes(),
          getCountries(),
          getOrgTypes(),
          getOrgSizes(),
          getMetaIndividual(),
          getMetaGeographic()
        ]);
        if (!active) return;

        setIsdOptions(codes);
        setCountryOptions(countries);
        setOrgTypeOptions(types);
        setOrgSizeOptions(sizes);
        setMetaIndividual(metaIndividuals)
        setMetaGeographic(metaGeographics)

        setForm((s) => {
          const next = { ...s };

          if (!next.country) {
            next.country = "";
          } else if (!valueExistsInOptions(next.country, countries)) {
            next.country = "";
          }

          if (!next.orgType) {
            next.orgType = "";
          } else if (!valueExistsInOptions(next.orgType, types)) {
            next.orgType = "";
          }

          if (!next.orgSize) {
            next.orgSize = "";
          } else if (!valueExistsInOptions(next.orgSize, sizes)) {
            next.orgSize = "";
          }

          const rawPhone = String(apiSeed?.contact_phone_number ?? "");
          const apiPhoneCode = String(apiSeed?.phone_code ?? "");
          const allDigits = rawPhone.replace(/\D/g, "");

          let chosenIsd = apiPhoneCode || "";
          let national = allDigits;

          const isdDigits = chosenIsd.replace(/\D/g, "");
          if (isdDigits && allDigits.startsWith(isdDigits)) {
            national = allDigits.slice(isdDigits.length);
          } else if (!chosenIsd && allDigits) {
            const match = codes.find((c) => {
              const cd = c.value.replace(/\D/g, "");
              return cd && allDigits.startsWith(cd);
            });
            if (match) {
              chosenIsd = match.value;
              national = allDigits.slice(match.value.replace(/\D/g, "").length);
            }
          }

          if (!next.phoneIsd) {
            next.phoneIsd =
              chosenIsd && valueExistsInOptions(chosenIsd, codes) ? chosenIsd : "";
          }
          if (!next.phone) next.phone = national || "";

          return next;
        });
      } catch (e: any) {
        if (!active) return;
        setError(e?.response?.data?.error ||
        e?.response?.data?.message ||
        e?.response?.data?.detail || "Failed to load metadata");
      }
    })();
    return () => {
      active = false;
    };
  }, [apiSeed?.contact_phone_number, apiSeed?.phone_code]);

  useEffect(() => {
    let active = true;
    if (!form.country) {
      setStateOptions([]);
      setCityOptions([]);
      return;
    }
    (async () => {
      try {
        const states = await getStates(form.country);
        if (!active) return;
        setStateOptions(states);
        setForm((s) => ({
          ...s,
          state: s.state && states.some((o) => o.value === s.state) ? s.state : "",
          city: "",
        }));
      } catch (e: any) {
        if (!active) return;
        setError(e?.response?.data?.error ||
        e?.response?.data?.message ||
        e?.response?.data?.detail|| "Failed to load states");
      }
    })();
    return () => {
      active = false;
    };
  }, [form.country]);

  useEffect(() => {
    let active = true;
    if (!form.country || !form.state) {
      setCityOptions([]);
      return;
    }
    (async () => {
      try {
        const cities = await getCities(form.country, form.state);
        if (!active) return;
        setCityOptions(cities);
        setForm((s) => ({
          ...s,
          city: s.city && cities.some((o) => o.value === s.city) ? s.city : "",
        }));
      } catch (e: any) {
        if (!active) return;
        setError(e?.response?.data?.error ||
        e?.response?.data?.message ||
        e?.response?.data?.detail || "Failed to load cities");
      }
    })();
    return () => {
      active = false;
    };
  }, [form.country, form.state]);

  useEffect(() => {
    const { valid, message } = validatePhoneByCode(form.phoneIsd, form.phone);
    setPhoneError(valid ? "" : message);
  }, [form.phoneIsd, form.phone]);

  useEffect(() => {
    if (form.orgType === "Individual" && form.age) {
      const ageNum = parseInt(form.age, 10);
      if (isNaN(ageNum)) {
        setAgeError("");
      } else if (ageNum < 18) {
        setAgeError("Age must be at least 18 years");
      } else if (ageNum > 100) {
        setAgeError("Age must be 100 years or less");
      } else {
        setAgeError("");
      }
    } else {
      setAgeError("");
    }
  }, [form.age, form.orgType]);

  const buildPayload = (): OrganizationDetails => {
    const seed = apiSeed ?? ({} as OrganizationDetails);
    const toNum = (v: string) => (v && /^\d{4}$/.test(v) ? Number(v) : null);
    const nationalDigits = form.phone.replace(/\D/g, "");
    const isdValue = form.phoneIsd || "";

    return {
      company_name: form.legalName ?? seed.company_name ?? "",
      entity_type: seed.entity_type ?? "Not Specified",
      organization_number: form.orgNumber || seed.organization_number || null,
      organization_type: form.orgType || seed.organization_type || "",
      sector: seed.sector ?? "",
      region: form.country || seed.region || "",
      address_line: form.streetAddress || seed.address_line || null,
      city: form.city || seed.city || null,
      state: form.state || seed.state || "",
      postal_code: form.postalCode || seed.postal_code || "",
      size: form.orgSize || seed.size || "",
      founded_year: toNum(form.foundedYear) ?? seed.founded_year ?? null,
      website_url: form.website || seed.website_url || null,
      contact_email: form.email || seed.contact_email || null,
      contact_phone_number: nationalDigits || null,
      phone_code: isdValue || null,
      extra_details: seed.extra_details ?? {},
      Number_of_Board_Members: seed.Number_of_Board_Members ?? null,
      Number_of_Employee: seed.Number_of_Employee ?? null,
      Number_of_Volunteers: seed.Number_of_Volunteers ?? null,

      age: form.age ? parseInt(form.age, 10) : undefined,
      gender: form.gender,
      ethnicity: form.ethnicity,
      disability_status: form.disability_status,
      veteran: form.veteran,
      immigration_status: form.immigration_status,
      socioeconomic_status: form.socioeconomic_status,
      rural_urban: form.rural_urban,
      operating_area: form.operatingScope || form.operating_area,
    };
  };

  const handleSave = async () => {
    setSaveButton(true);
    
    if (ageError) {
      toast.error(ageError);
      return;
    }

    const { valid, message } = validatePhoneByCode(form.phoneIsd, form.phone);
    if (!valid) {
      setPhoneError(message);
      toast.error(message);
      return;
    } else if (!form.legalName || !form.email) {
      const missingFields: string[] = [];

      if (!form.legalName) missingFields.push("legal name");
      if (!form.email) missingFields.push("email");

      toast.error(`Please enter your ${missingFields.join(" and ")}`);
      return;
    }

    try {
      setSaving(true);
      setError(null);
      const updated = await putProfileDetails(buildPayload());
      setApiSeed(updated);
      setFieldErrors({});     
setSaveButton(false);  
      updateOnboardingDraft(userKey, {
        organizationType: form.orgType,
        organizationName: form.legalName,
        country: form.country,
        postcode: form.postalCode,
        operatingScope: form.operatingScope || form.operating_area || "",
        websiteUrl: form.website,
      });
      toast.success("Profile details updated successfully");
    } catch (e: any) {
      const data = e?.response?.data;

      let hasShown = false;

      if (typeof data === "string") {
        toast.error(data, {
          duration: 5000
        });
        hasShown = true;
      } else if (data?.error|| data?.detail) {
        toast.error(data.error||data?.detail, {
          duration: 5000,
        });
        hasShown = true;
      } else if (typeof data === "object") {
        const errors: Record<string, boolean> = {}

        Object.keys(data).forEach((key) => {
          if (Array.isArray(data[key]) && data[key].length > 0) {
            errors[key] = true
            toast.error(data[key][0], {
              duration: 5000
            });
            if (!hasShown) {
              toast.error(data[key][0], { duration: 5000 });
              hasShown = true;
            }
          }
        });
        setFieldErrors(errors)
      }

      if (!hasShown) {
        toast.error("Failed to update details");
      }
    } finally {
      setSaving(false);
    }
  };

  const handleFile = async (file: File) => {
    try {
      setLogoUploading(true);
      setError(null);

      await uploadProfileFile(file, "profile_photo");

      const updated = await getProfileDetails();

      setApiSeed(updated);
      const finalUrl = updated.profile_photo || null;
      setLogoUrl(finalUrl);
      syncHeaderAvatar(finalUrl);

      toast.success("Logo uploaded");
    } catch (e: any) {
      // console.error(e);
      setError(
       e?.response?.data?.error ||
        e?.response?.data?.message ||
        e?.response?.data?.detail||
        "Logo upload failed"
      );
      toast.error("Logo upload failed");
    } finally {
      setLogoUploading(false);
    }
  };

  const handleRemoveLogo = async () => {
    try {
      setLogoUploading(true);
      setLogoUrl(null);
      const payload = deleteLogo();
      setLogoUrl(null);
      setApiSeed((prev) => (prev ? { ...prev, profile_photo: null } : prev));
      syncHeaderAvatar(null);
      toast.success("Logo removed");
    } catch {
      toast.error("Failed to remove logo");
      setLogoUrl(apiSeed?.profile_photo || null);
    } finally {
      setLogoUploading(false);
    }
  };

  const handleNextDetails = async () => {
    await handleSave();
    navigate("/dashboard/profile/objective");
  };

  return (
    <div className="flex min-h-screen gap-5 px-3">
      <ProfileSidebar isProfileRoute={isProfileRoute} />
      <div className="flex-1 min-w-0 overflow-y-auto">
        <Card>
          <div className="px-6 py-5">
            <Stepper active="details" />
          </div>

          {loading && (
            <div className="px-6 -mt-2 pb-2 text-sm text-[#6b7280]">
              Loading profile…
            </div>
          )}
          {error && <div className="px-6 -mt-2 pb-2 text-sm text-red-600">{error}</div>}

          <div className="px-6 pb-6">
            <div className="text-xl font-semibold mb-3">Organization Details</div>

            <div className="grid grid-cols-1 gap-4">
              {/* --- logo --- */}
              <div className="mb-4">
                <div className="text-sm mb-2">Organization Logo</div>
                <div className="flex flex-col md:flex-row gap-4 md:items-center">
                  <UploadDropzone onFile={handleFile} uploading={logoUploading} />
                  <div className="mt-10 md:mt-0 grid place-items-center relative">
                    {logoUrl ? (
                      <div className="relative">
                        <img
                          src={logoUrl}
                          alt="Organization Logo"
                          className="h-11 w-11 rounded-full border border-black object-cover"
                        />
                        <button
                          type="button"
                          onClick={handleRemoveLogo}
                          title="Remove logo"
                          className="absolute -top-2 -right-2 h-6 w-6 rounded-full bg-white border border-[#E5E7EB] shadow flex items-center justify-center"
                        >
                          <span className="material-icons text-[16px] text-[#6B7280]">close</span>
                        </button>
                      </div>
                    ) : (
                      <Avatar />
                    )}
                  </div>
                </div>
              </div>

              {/* --- form fields --- */}
              <div className="grid md:grid-cols-2 gap-4 gap-x-5">
                <div>
                  <div
                    className={`text-sm font-semibold mb-1 ${saveButton && (fieldErrors.company_name || !form.legalName) ? "text-red-600" : "text-black"
                      }`}
                  >
                    Organization Legal Name*
                  </div>
                  <Input
                    placeholder="e.g. ABC"
                    value={form.legalName}
                    onChange={(e) =>
                      setForm((s) => ({ ...s, legalName: e.target.value }))
                    }
                  />
                </div>

                <div>
                  <div className="text-sm font-semibold mb-1">
                    Registered Organization Number
                  </div>
                  <Input
                    placeholder="XX-XXXXXXXX"
                    value={form.orgNumber}
                    type="number"
                    onChange={(e) =>
                      setForm((s) => ({ ...s, orgNumber: e.target.value }))
                    }
                  />
                </div>

                <div>
                  <div className="text-sm font-semibold mb-1">Year of Establishment</div>
                  <Input
                    placeholder="e.g. 2020"
                    value={form.foundedYear}
                    onChange={(e) =>
                      setForm((s) => ({
                        ...s,
                        foundedYear: e.target.value.replace(/\D/g, "").slice(0, 4),
                      }))
                    }
                  />
                </div>

                <div>
                  <div className="text-sm font-semibold mb-1">Website</div>
                  <Input
                    placeholder="e.g. example.com"
                    value={form.website}
                    onChange={(e) =>
                      setForm((s) => ({ ...s, website: e.target.value }))
                    }
                  />
                </div>

                {/* Organization Type */}
                <div>
                  <div className="text-sm font-semibold mb-1">Organization Type</div>
                  <Select
                    value={safeOrgType}
                    onChange={(e) => setForm((s) => ({ ...s, orgType: e.target.value }))}
                  >
                    <option value="" disabled>Select organization type</option>
                    {orgTypeOptions.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                    {!orgTypeOptions.length && <option value="Small Business">Small Business</option>}
                  </Select>
                </div>

                {/* Organization Size */}
                <div>
                  <div className="text-sm font-semibold mb-1">Organization Size</div>
                  <Select
                    value={safeOrgSize}
                    onChange={(e) => setForm((s) => ({ ...s, orgSize: e.target.value }))}
                  >
                    <option value="" disabled>Select organization size</option>
                    {orgSizeOptions.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                    {!orgSizeOptions.length && <option>1-10</option>}
                  </Select>
                </div>

                {/* Individual Demographics - Only show when Organization Type is "Individual" */}
                {form.orgType === "Individual" && (
                  <>
                    {/* Age */}
                    <div>
                      <div className="text-sm font-semibold mb-1">Age</div>
                      <Input
                        type="number"
                        placeholder="Enter your age"
                        value={form.age || ""}
                        min={18}
                        max={100}
                        onChange={(e) => {
                          setForm((s) => ({ ...s, age: e.target.value }))
                        }}
                        className={ageError ? "border-red-500" : ""}
                      />
                      {ageError && (
                        <div className="text-xs text-red-600 mt-1">{ageError}</div>
                      )}
                    </div>

                    {/* Gender */}
                    <div>
                      <div className="text-sm font-semibold mb-1">Gender</div>
                      <Select
                        value={
                          form.gender && metaIndividual?.fields.gender
                            ? metaIndividual.fields.gender.some((o) => o.value === form.gender)
                              ? form.gender
                              : ""
                            : ""
                        }
                        onChange={(e) =>
                          setForm((s) => ({ ...s, gender: e.target.value }))
                        }
                      >
                        <option value="" disabled>Select gender</option>
                        {metaIndividual?.fields.gender.map((o, idx) => (
                          <option key={idx} value={o.value}>{o.label}</option>
                        ))}
                      </Select>
                    </div>

                    <div className="">
                      <div className="text-sm font-semibold mb-2">Race / Ethnicity</div>
                      <Select
                        value={hasValue(form.ethnicity || "", metaIndividual?.fields.ethnicity) ? form.ethnicity || "" : ""}
                        onChange={(e) =>
                          setForm((s) => ({ ...s, ethnicity: e.target.value }))
                        }
                      >
                        <option value="" disabled>Select ethnicity</option>
                        {metaIndividual?.fields.ethnicity.map((o, idx) => (
                          <option key={idx} value={o}>{o}</option>
                        ))}
                      </Select>
                    </div>

                    {/* Disability Status */}
                    <div>
                      <div className="text-sm font-semibold mb-1">Disability</div>
                      <Select
                        value={
                          form.disability_status && metaIndividual?.fields.disability_status
                            ? metaIndividual.fields.disability_status.some((o) => o.value === form.disability_status)
                              ? form.disability_status
                              : ""
                            : ""
                        }
                        onChange={(e) =>
                          setForm((s) => ({ ...s, disability_status: e.target.value }))
                        }
                      >
                        <option value="" disabled>Select disability status</option>
                        {metaIndividual?.fields.disability_status.map((o, idx) => (
                          <option key={idx} value={o.value}>{o.label}</option>
                        ))}
                      </Select>
                    </div>

                    {/* Veteran Status */}
                    <div>
                      <div className="text-sm font-semibold mb-1">Veteran Status</div>
                      <Select
                        value={
                          form.veteran && metaIndividual?.fields.veteran
                            ? metaIndividual.fields.veteran.some((o) => o.value === form.veteran)
                              ? form.veteran
                              : ""
                            : ""
                        }
                        onChange={(e) =>
                          setForm((s) => ({ ...s, veteran: e.target.value }))
                        }
                      >
                        <option value="" disabled>Select veteran status</option>
                        {metaIndividual?.fields.veteran.map((o, idx) => (
                          <option key={idx} value={o.value}>{o.label}</option>
                        ))}
                      </Select>
                    </div>

                    {/* immigration_status Status */}
                    <div>
                      <div className="text-sm font-semibold mb-1">Immigration Status</div>
                      <Select
                        value={hasValue(form.immigration_status || "", metaIndividual?.fields.immigration_status) ? form.immigration_status || "" : ""}
                        onChange={(e) =>
                          setForm((s) => ({ ...s, immigration_status: e.target.value }))
                        }
                      >
                        <option value="" disabled>Select immigration status</option>
                        {metaIndividual?.fields.immigration_status.map((o, idx) => (
                          <option key={idx} value={o}>{o}</option>
                        ))}
                      </Select>
                    </div>

                    {/* Socioeconomic Background */}
                    <div>
                      <div className="text-sm font-semibold mb-2">Socioeconomic Background</div>
                      <Select
                        value={hasValue(form.socioeconomic_status || "", metaIndividual?.fields.socioeconomic_status) ? form.socioeconomic_status || "" : ""}
                        onChange={(e) =>
                          setForm((s) => ({ ...s, socioeconomic_status: e.target.value }))
                        }
                      >
                        <option value="" disabled>Select socioeconomic background</option>
                        {metaIndividual?.fields.socioeconomic_status.map((o, idx) => (
                          <option key={idx} value={o}>{o}</option>
                        ))}
                      </Select>
                    </div>
                  </>
                )}

                <div>
                  <div
                    className={`text-sm font-semibold mb-1 ${saveButton && (fieldErrors.contact_email || !form.email)
                        ? "text-red-600"
                        : "text-black"
                      }`}
                  >
                    Email*
                  </div>

                  <Input
                    placeholder="abc@example.com"
                    value={form.email}
                    onChange={(e) =>
                      setForm((s) => ({ ...s, email: e.target.value }))
                    }
                  />
                </div>

                <div>
                  <div className="text-sm font-semibold mb-1">Phone Number</div>
                  <div className="flex gap-2">
                    <Select
                      className="w-[140px]"
                      value={safePhoneIsd}
                      onChange={(e) =>
                        setForm((s) => ({ ...s, phoneIsd: e.target.value }))
                      }
                    >
                      <option value="" disabled>Select country code</option>
                      {isdOptions.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </Select>
                    <Input
                      placeholder="1234567890"
                      value={form.phone}
                      onChange={(e) =>
                        setForm((s) => ({
                          ...s,
                          phone: e.target.value.replace(/\D/g, ""),
                        }))
                      }
                      onKeyDown={(e) => {
                        const allowed = [
                          "Backspace",
                          "Delete",
                          "ArrowLeft",
                          "ArrowRight",
                          "Tab",
                          "Home",
                          "End",
                        ];
                        if (e.ctrlKey || e.metaKey) return;
                                        if (allowed.includes(e.key)) return;
                        if (!/^\d$/.test(e.key)) e.preventDefault();
                      }}
                      inputMode="numeric"
                      pattern="[0-9]*"
                      type="tel"
                      className={phoneError ? "border-red-500" : ""}
                    />
                  </div>
                  {phoneError && (
                    <div className="text-xs text-red-600 mt-1">{phoneError}</div>
                  )}
                </div>

                <div>
                  <div className="text-sm font-semibold mb-1">Country / Region</div>
                  <Select
                    value={safeCountry}
                    onChange={(e) =>
                      setForm((s) => ({
                        ...s,
                        country: e.target.value,
                        state: "",
                        city: "",
                      }))
                    }
                  >
                    <option value="" disabled>Select country/region</option>
                    {countryOptions.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </Select>
                </div>

                <div>
                  <div className="text-sm font-semibold mb-1">State / Province</div>
                  <Select
                    value={safeState}
                    onChange={(e) =>
                      setForm((s) => ({ ...s, state: e.target.value, city: "" }))
                    }
                  >
                    <option value="" disabled>Select state/province</option>
                    {stateOptions.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </Select>
                </div>

                <div>
                  <div className="text-sm font-semibold mb-1">City</div>
                  <Select
                    value={safeCity}
                    onChange={(e) =>
                      setForm((s) => ({ ...s, city: e.target.value }))
                    }
                  >
                    <option value="" disabled>Select city</option>
                    {cityOptions.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </Select>
                </div>

                <div>
                  <div className="text-sm font-semibold mb-1">ZIP / Postal</div>
                  <Input
                    value={form.postalCode}
                    onChange={(e) =>
                      setForm((s) => ({ ...s, postalCode: e.target.value }))
                    }
                  />
                </div>

                <div className="md:col-span-1">
                  <div className="text-sm font-semibold mb-1">Street Address</div>
                  <Input
                    value={form.streetAddress}
                    onChange={(e) =>
                      setForm((s) => ({ ...s, streetAddress: e.target.value }))
                    }
                  />
                </div>
                <div>
                  <div className="text-sm font-semibold mb-1">Rural/Urban</div>
                  <Select
                    value={hasValue(form.rural_urban || "", metaGeographic?.rural_urban) ? form.rural_urban || "" : ""}
                    onChange={(e) =>
                      setForm((s) => ({ ...s, rural_urban: e.target.value }))
                    }
                  >
                    <option value="" disabled>Select rural/urban</option>
                    {metaGeographic?.rural_urban.map((o, idx) => (
                      <option key={idx} value={o}>{o}</option>
                    ))}
                  </Select>
                </div>
                <div className="">
                  <div className="text-sm font-semibold mb-2">Operating Scope</div>
                  <Select
                    value={
                      hasValue(form.operatingScope || form.operating_area || "", metaGeographic?.operating_area)
                        ? form.operatingScope || form.operating_area || ""
                        : ""
                    }
                    onChange={(e) =>
                      setForm((s) => ({
                        ...s,
                        operatingScope: e.target.value,
                        operating_area: e.target.value,
                      }))
                    }
                  >
                    <option value="" disabled>Select operating scope</option>
                    {metaGeographic?.operating_area.map((o, idx) => (
                      <option key={idx} value={o}>{o}</option>
                    ))}
                  </Select>
                </div>
              </div>

              <div className="mt-6 flex justify-end gap-3">
                <Button
                  className="min-w-[96px]"
                  disabled={saving || !!phoneError}
                  onClick={handleSave}
                >
                  {saving ? "Saving…" : "Save"}
                </Button>
                <Button className="min-w-[96px] gap-2" disabled={saving || !!phoneError} onClick={handleNextDetails}>
                  Next <ArrowRight />
                </Button>
              </div>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}
